<template>
    <div class="m-2">
        <ReplyDocumentPreview
            ref="preview"
            :file="previewFile"
            :request="request"
        />
        <b-row>
            <b-col md="6" lg="6">
                <div class="pb-1 pt-1">
                    <h3>{{ $t('Page(SentRequest):Component(Text):product-information') }}</h3>
                </div>
                <table>
                    <tbody>
                    <tr>
                        <th>{{ supplierNameLabel }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.name }}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ supplierIdLabel }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.id }}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ scipNameLabel }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.scipName }}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ scipNumberLabel }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.scipId }}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ $t('Page(SentRequest):Component(Text):product-information-categories') }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.categories.map(c => c.code).filter(c => c).join(', ') }}</span>
                        </td>
                    </tr>
                    <tr>
                        <th>{{ $t('Component(Label):manufacturing-origin') }}:</th>
                        <td>
                            <span class="alert-warning pl-1 pr-1">{{ reply.product.countryOfOrigin ? reply.product.countryOfOrigin.title : '' }}</span>
                        </td>
                    </tr>
                    <tr v-if="reply.product.weight">
                        <th>{{ $t('Component(Placeholder):product-weight') }}</th>
                        <td><span class="pl-1 pr-1">{{ reply.product.weight }}</span></td>
                    </tr>
                    </tbody>
                </table>
            </b-col>
            <b-col md="6" lg="6">
                <Conversation key="sent-request-reply" :request-id="requestId" />
            </b-col>
        </b-row>
        <b-row>
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):composition') }}</h3></div>
        </b-row>
        <b-row>
            <b-col>
                <ComponentConcernElements
                    class="mb-1"
                    v-for="container in reply.componentConcernElements"
                    :container="container"
                    :substance-lists="substanceLists"
                />
                <b-alert
                    variant="warning"
                    :show="showConcernSubstancesAreNotDefined"
                >
                    <div class="alert-body">
                        <span>{{ $t('Component(Text):concern-substances-not-defined') }}</span>
                    </div>
                </b-alert>
            </b-col>
        </b-row>
        <div v-if="showSubstanceListsTable">
            <b-row>
                <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):replied-concern-elements') }}</h3></div>
            </b-row>
            <b-row>
                <b-col>
                    <ConcernElements
                        :product="{}"
                        :show-information-owner-settings="false"
                        :company-has-information="true"
                        :legislations="substanceListsTableLegislations"
                        :show-instruction="false"
                        :show-substance-list-instruction="false"
                        :show-no-single-parts-checkbox="false"
                        :show-questions="false"
                        :auto-expand-lists="request.legislations.length === 1"
                        :auto-expand-substances="request.legislations.length === 1"
                        :read-only="true"
                        container-class=""
                        :included-substances="simpleFlowIncludedSubstances"
                        :excluded-substances="simpleFlowExcludedSubstances"
                    />
                </b-col>
            </b-row>
        </div>
        <b-row>
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):verification') }}</h3></div>
        </b-row>

        <b-row>
            <b-col>
                <b-card no-body :border-variant="compliantStatementStyle.border" :bg-variant="compliantStatementStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):compliant-statement-label') }}</b-card-title>
                    </b-card-header>
                    <CompliantStatementTable
                        :documents="reply.compliantStatementDocuments"
                        :empty-message-bg="compliantStatementStyle.alert"
                        :selectable="false"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body :border-variant="docStyle.border" :bg-variant="docStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):doc-label') }}</b-card-title>
                    </b-card-header>
                    <DeclarationOfConformityTable
                        :documents="reply.declarationOfConformityDocuments"
                        :selectable="false"
                        :empty-message-bg="docStyle.alert"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body :border-variant="evidenceReportStyle.border" :bg-variant="evidenceReportStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):evidence-report-label') }}</b-card-title>
                    </b-card-header>
                    <EvidenceReportTable
                        :documents="reply.evidenceReportDocuments"
                        :selectable="false"
                        :empty-message-bg="evidenceReportStyle.alert"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row class="mb-1">
            <b-col>
                <b-card-group deck>
                    <Instruction
                        :instruction="safeUseInstruction"
                        :title="$t('Component(Verification):safe-use-label')"
                        :state="safeUseState"
                        :empty-text="getSafeUseEmptyText()"
                    />
                    <Instruction
                        :instruction="reply.dissemblingInstruction"
                        :title="$t('Component(Verification):dissembling-label')"
                        :state="dissemblingState"
                        @download="download"
                        @preview="preview"
                    />
                </b-card-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body border-variant="light">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Text):comment-from-supplier') }}</b-card-title>
                    </b-card-header>
                    <div class="card-body">
                        <p v-if="reply.informationToCustomer" style="white-space: pre-line;">{{ reply.informationToCustomer }}</p>
                        <b-alert
                            variant="secondary"
                            :show="!reply.informationToCustomer"
                        >
                            <div class="alert-body">
                                <span>{{ $t('Component(Text):no-comment-from-supplier') }}</span>
                            </div>
                        </b-alert>
                    </div>
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body border-variant="light">
                    <b-card-header>
                        <b-card-title>{{ $t('Page(SentRequest):Component(Text):validity-of-information') }}</b-card-title>
                    </b-card-header>
                    <div class="card-body">
                        <p>
                            <strong>{{ reply.validityOfInformation }} {{ $t('Component(Text):months') }}</strong>
                        </p>
                    </div>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {
    BCard, BRow, BCol, BFormCheckbox, BFormTextarea,
    BFormDatepicker, BCardGroup, BFormGroup, BFormInput, BAlert,
    BBadge, BCardTitle, BCardHeader, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ConcernElement from "@/views/request/sent/components/ConcernElement";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import Instruction from "@/views/product/components/product-card/verification/Instruction.vue";
import {Conversation} from "@core/components/messenger";
import ReplyTabProductSection from "@core/mixins/ui/reply-tab-product-section";
import {VerificationDocumentType} from "@/libs/product";
import ComponentConcernElements from "@/views/request/sent/components/ComponentConcernElements";
import {
    CompliantStatementTable, EvidenceReportTable, DeclarationOfConformityTable
} from "@/views/product/components/product-card/verification";
import ReplyDocumentPreview from "@/views/request/sent/components/ReplyDocumentPreview.vue";
import downloadProductDocument from "@core/mixins/download-product-document";
import {ConcernElements} from "@/views/product/components/product-card";

export default {
    mixins: [ReplyTabProductSection, downloadProductDocument],
    components: {
        ConcernElements,
        ReplyDocumentPreview,
        CompliantStatementTable,
        BCard, BRow, BCol, Conversation,
        BFormCheckbox, BListGroup, BListGroupItem,
        BFormTextarea, BFormDatepicker, BCardGroup, BCardTitle,
        BFormGroup, BFormInput, BAlert, BBadge, BCardHeader,
        ConcernElement, ComponentConcernElements,
        DeclarationOfConformityTable,
        EvidenceReportTable,
        DeclarationApproval,
        Instruction,
        vSelect,
    },
    props: {
        reply: {
            type: Object,
            required: true
        },
        request: {
            type: Object,
            required: true,
        },
        substanceLists: {
            type: Array,
            default() {
                return [];
            }
        },
    },
    data() {
        return {
            requestId: this.request.id,
            previewFile: null,
        };
    },
    methods: {
        getSafeUseEmptyText() {
            if (false === this.reply.product.isSafeUseApplicable) {
                return this.$t('Component(Text):safe-use-applicability')
            }

            return 'There is no Safe use instructions.';
        },

        download(document, done) {
            this
                .downloadRepliedDocument(this.request.id, document)
                .then(done);
        },

        preview(document) {
            this.previewFile = document;
            this.$nextTick(() => {
                this.$refs.preview.show();
            });
        }
    },

    computed: {
        showSubstanceListsTable() {
            return this.request && this.request.replyProcessFlow === this.$store.getters.replyProcessFlow.SIMPLE;
        },
        substanceListsTableLegislations() {
            if (!this.showSubstanceListsTable) {
                return [];
            }
            return this.request.legislations.map(l => l.id);
        },
        simpleFlowIncludedSubstances() {
            if (!this.reply || this.reply.componentConcernElements.length === 0) {
                return [];
            }

            return this.reply.componentConcernElements[0].substances;
        },
        simpleFlowExcludedSubstances() {
            if (!this.reply || this.reply.componentConcernElements.length === 0) {
                return [];
            }

            return this.reply.componentConcernElements[0].excludedSubstances.map(id => {
                return {
                    substance: {id},
                    status: 'excluded',
                };
            });
        },
        safeUseInstruction() {
            if (false === this.reply.product.isSafeUseApplicable) {
                return null;
            }

            return this.reply.safeUseInstruction;
        },
        showConcernSubstancesAreNotDefined() {
            if (null === this.reply.product.hasConcernElements && 0 === this.reply.componentConcernElements.length) {
                return true;
            }

            if (this.reply.product.hasConcernElements === true) {
                return this.reply.componentConcernElements.length === 0;
            }

            return false;
        },

        evidenceReportStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.reply.evidenceReportDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        docStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                state = 'default';
            } else if (this.reply.declarationOfConformityDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.reply.compliantStatementDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        safeUseState() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                return 'default';
            }
            if (false === this.reply.isSafeUseApplicable) {
                return 'success';
            }
            return (this.reply.safeUseInstruction && this.reply.safeUseInstruction.id) ? 'success' : 'required';
        },
        dissemblingState() {
            if (!this.request.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                return 'default';
            }

            return (this.reply.dissemblingInstruction && this.reply.dissemblingInstruction.id) ? 'success' : 'required';
        },
    },
    watch: {
        request(r) {
            this.requestId = r.id;
        }
    }
}
</script>

<style type="text/css">
table tbody th {
    text-align: right;
    padding: 4px;
}
</style>
